import React, { useMemo } from 'react'
import { useXAxis, useYAxis } from '@mui/x-charts/hooks'
import { EventItem } from './CompanyEvents'
import { AxisScaleConfig, ScaleName } from '@mui/x-charts/internals'
import { Theme, useTheme } from '@mui/material/styles'
import { useLineSeries } from '@mui/x-charts/hooks'
import IssueTypeLabels, { getIssueTypeColors, HasWarrantLabel } from '../../../../components/entities/IssueTypeLabels'
import { isAfter, isSameDay } from 'date-fns'
import { LabelChip } from '../../../../components/entities/IssueTypeLabels'
import { DefaultizedLineSeriesType } from '@mui/x-charts/models'
import { ComputedXAxis } from '@mui/x-charts/internals'
import { findSubscriptionPeriodCloseToDate } from '../../../../utils/warrants'
import { ShareCapitalChange } from 'types/CompanyEntity'
import { IssueEntity } from 'types/IssueEntity'
import { WarrantListItem } from '@finance-lab/utils/types'

type ElemPosition = {
    height: number
    top: number
    left: number
    width: number
}

type EventPosition = {
    button: ElemPosition
    pin: ElemPosition
    zIndex: number
}
type Occupations = Record<'x' | 'y', Record<string, number[]>>
export type EventWithPosition = EventItem & { position: EventPosition }

const BUTTON_SIZE = { width: 36, height: 20 }

const addOccupation = (occupations: Occupations) => (id: string, position: EventPosition['button']) => {
    {
        const vals: number[] = []

        for (let i = position.left; i <= position.left + position.width; i++) {
            vals.push(i)
        }

        occupations.x[id] = vals
    }
    {
        const vals: number[] = []

        for (let i = position.top; i <= position.top + position.height; i++) {
            vals.push(i)
        }

        occupations.y[id] = vals
    }
}

const BASE_Z_INDEX = 100
const OFFSET_TOP = -20

const calculatePosition =
    (
        occupations: Occupations,
        xScale: AxisScaleConfig[ScaleName]['scale'],
        yScale: AxisScaleConfig[ScaleName]['scale'],
    ) =>
    (event: EventItem): EventPosition | null => {
        if (event.y === undefined || event.y === undefined) {
            return null
        }

        const yScalePosition = yScale(event.y)
        const xScalePosition = xScale(event.x)

        if (yScalePosition === undefined || xScalePosition === undefined) return null

        const { [event.id]: myself, ...occupationsById } = occupations.x
        const roundedLeft = Math.max(-4, Math.round(xScalePosition - BUTTON_SIZE.width / 2))
        const roundedTop = Math.round(yScalePosition) + OFFSET_TOP
        let occupationsCount: number | null = null
        let top: number = roundedTop
        let elevationCount = 0

        while (occupationsCount !== 0) {
            top = top - (occupationsCount ?? 0)

            occupationsCount = Object.keys(occupationsById).filter(
                (_id) =>
                    _id !== event.id &&
                    occupations.x[_id].some((v) => v >= roundedLeft + 6 && v <= roundedLeft + BUTTON_SIZE.width) &&
                    occupations.y[_id].some((v) => v >= top && v <= top + BUTTON_SIZE.height),
            ).length
            elevationCount++
        }

        addOccupation(occupations)(event.id, {
            left: roundedLeft,
            width: BUTTON_SIZE.width,
            top: top,
            height: BUTTON_SIZE.height,
        })

        return {
            button: {
                left: roundedLeft,
                width: BUTTON_SIZE.width,
                height: BUTTON_SIZE.height,
                top,
            },
            pin: {
                height: yScalePosition - (top + BUTTON_SIZE.height + OFFSET_TOP) + 1,
                top: top + BUTTON_SIZE.height,
                left: xScalePosition - 1,
                width: 2,
            },
            zIndex: Math.max(1, BASE_Z_INDEX - elevationCount),
        }
    }

// If the Y value is null, find the closest non-null Y value
const getClosestYValue = (ySeriesData: readonly (number | null)[], index: number) => {
    if (ySeriesData[index] !== null) return ySeriesData[index]

    let closestValue = null
    let step = 1
    const limit = 10 // Set limit to avoid infinite loop

    while (closestValue === null && step < limit) {
        closestValue = ySeriesData[index - step] ?? ySeriesData[index + step]

        step++
    }

    return closestValue
}

const getPositionForEvent = (
    xAxis: ComputedXAxis,
    lineSeriesData: DefaultizedLineSeriesType['data'],
    date: string | null,
) => {
    if (!xAxis.data) return null
    if (date === null) return null

    {
        const d = new Date(date)

        if (d < xAxis.data[0]) return null
        if (d > xAxis.data[xAxis.data.length - 1]) return null
    }

    const primaryIndex = xAxis.data.findIndex((d: Date) => isSameDay(d, new Date(date)))
    const index = primaryIndex > -1 ? primaryIndex : xAxis.data.findIndex((d: Date) => isAfter(d, new Date(date)))

    if (index === -1) {
        return null
    }

    const y = getClosestYValue(lineSeriesData, index)

    if (y === null) {
        return null
    }

    return {
        x: xAxis.data[index],
        y,
    }
}

const getEventFromIssue =
    (theme: Theme, xAxis: ComputedXAxis, lineSeriesData: DefaultizedLineSeriesType['data']) =>
    (issue: IssueEntity): EventItem | null => {
        const date = issue.ipoTradingDate || issue.publicationDate
        const position = getPositionForEvent(xAxis, lineSeriesData, date)

        if (position === null) return null

        const { backgroundColor = '#000000' } = getIssueTypeColors(theme, issue.type) || {}

        return {
            x: position.x,
            y: position.y,
            label: (
                <IssueTypeLabels
                    isRto={!!issue.rto}
                    isSpac={!!issue.spac}
                    issueType={issue.type}
                    isSingleLabel
                    noTooltip
                />
            ),
            backgroundColor: backgroundColor,
            type: 'issue',
            id: issue.id,
            category: issue.type,
        }
    }

const getEventFromWarrant =
    (theme: Theme, xAxis: ComputedXAxis, lineSeries: DefaultizedLineSeriesType['data']) =>
    (warrant: WarrantListItem): EventItem | null => {
        const latestPeriod = findSubscriptionPeriodCloseToDate(warrant, new Date().toISOString())
        const date = latestPeriod?.subEndDate ?? null
        const position = getPositionForEvent(xAxis, lineSeries, date)

        if (position === null) return null

        return {
            x: position.x,
            y: position.y,
            label: <HasWarrantLabel warrant={warrant} noTooltip />,
            backgroundColor: theme.palette.TO.main,
            type: 'warrant',
            category: 'warrant',
            id: warrant.id,
        }
    }

const getEventFromSplit =
    (theme: Theme, xAxis: ComputedXAxis, lineSeries: DefaultizedLineSeriesType['data']) =>
    (split: ShareCapitalChange): EventItem | null => {
        const date = split.changeDate
        const position = getPositionForEvent(xAxis, lineSeries, date)

        if (position === null) return null

        return {
            id: `${split.type.replace(' ', '_')}-${split.changeDate}`,
            x: position.x,
            y: position.y,
            label: <LabelChip label='SPLIT' sx={{ backgroundColor: 'black', color: 'white' }} />,
            backgroundColor: 'black',
            type: 'split',
            category: split.type,
        }
    }

export const useEvents = (
    issues: IssueEntity[] | null,
    warrants: WarrantListItem[] | null,
    shareCapitalChanges: ShareCapitalChange[] | null,
) => {
    const lineSeries = useLineSeries('trades')
    const xAxis = useXAxis()
    const theme = useTheme()
    const xAxisScale = useXAxis()
    const yAxisScale = useYAxis()

    const result = useMemo(() => {
        if (!warrants && !issues && !shareCapitalChanges) return []

        const events = []
        {
            const issueCreator = getEventFromIssue(theme, xAxis, lineSeries?.data || [])
            for (let i = 0; i < (issues?.length ?? 0); i++) {
                const event = issueCreator(issues[i])
                if (!event) continue
                events.push(event)
            }

            const warrantCreator = getEventFromWarrant(theme, xAxis, lineSeries?.data || [])
            for (let i = 0; i < (warrants?.length ?? 0); i++) {
                const event = warrantCreator(warrants[i])
                if (!event) continue
                events.push(event)
            }

            const splitsCreator = getEventFromSplit(theme, xAxis, lineSeries?.data || [])
            for (let i = 0; i < (shareCapitalChanges?.length ?? 0); i++) {
                const event = splitsCreator(shareCapitalChanges[i])
                if (!event) continue
                events.push(event)
            }
        }

        let clusteredEvents: EventWithPosition[]

        {
            const occupations: Occupations = { x: {}, y: {} }
            const positionedEvents: EventWithPosition[] = []
            for (let i = 0; i < events.length; i++) {
                const position = calculatePosition(occupations, xAxisScale.scale, yAxisScale.scale)(events[i])

                if (position === null) {
                    console.warn(`Couldn't find position for event.`, { event: events[i] })
                    continue
                }

                positionedEvents.push({ ...events[i], position })
            }

            clusteredEvents = clusterEvents(positionedEvents, occupations)
        }

        const positionedEventsWithClusters: EventWithPosition[] = []
        {
            const newOccupations: Occupations = { x: {}, y: {} }
            for (let i = 0; i < clusteredEvents.length; i++) {
                const position = calculatePosition(
                    newOccupations,
                    xAxisScale.scale,
                    yAxisScale.scale,
                )(clusteredEvents[i])

                if (position === null) {
                    console.warn(`Couldn't find position for event.`, { event: clusteredEvents[i] })
                    continue
                }

                positionedEventsWithClusters.push({ ...clusteredEvents[i], position })
            }
        }

        return positionedEventsWithClusters
    }, [issues, lineSeries?.data, shareCapitalChanges, theme, warrants, xAxis, xAxisScale.scale, yAxisScale.scale])

    return result
}

const clusterEvents = (events: EventWithPosition[], occupations: Occupations) => {
    const clusters: Record<string, EventWithPosition[]> = {}

    for (const eventId of Object.keys(occupations.x)) {
        const event = events.find((e) => e.id === eventId)

        if (!event) {
            console.warn(`Couldn't find event ${eventId}`)
            continue
        }

        const [startPos] = occupations.x[eventId]
        const endPos = occupations.x[eventId][occupations.x[eventId].length - 1]
        const clusterCandidates = Object.keys(occupations.x).filter(
            (_id) =>
                _id !== eventId &&
                event.category === events.find((e) => e.id === _id)?.category &&
                occupations.x[_id].some((v) => v >= startPos - 0 && v <= endPos + 0),
        )

        if (clusterCandidates.length === 0) continue

        const clusterName = `cluster-${clusterCandidates[0]}`

        if (!clusters[clusterName]) {
            clusters[clusterName] = []
        }

        clusters[clusterName].push(event)
    }

    const clusterEvents: EventWithPosition[] = []
    for (const clusterName of Object.keys(clusters)) {
        if (clusters[clusterName].length < 3) {
            delete clusters[clusterName]
            continue
        }

        const [referenceEvent] = clusters[clusterName]

        clusterEvents.push({
            id: clusterName,
            backgroundColor: referenceEvent.backgroundColor,
            x: referenceEvent.x,
            y: referenceEvent.y,
            label: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        aspectRatio: '1 / 1',
                        transform: 'translateY(-30%)',
                        borderRadius: '50%',
                        background: referenceEvent.backgroundColor,
                        color: '#fff',
                    }}
                >
                    {clusters[clusterName].length}
                </div>
            ),
            category: referenceEvent.category,
            type: 'cluster',
            position: referenceEvent.position,
        })
    }

    const eventIdsToCluster = Object.values(clusters)
        .flat()
        .map((e) => e.id)

    return events.filter((e) => !eventIdsToCluster.includes(e.id)).concat(...clusterEvents)
}
